import * as Sentry from '@sentry/react-router';
import { isRouteErrorResponse, useParams, useRouteError } from 'react-router';
import { MessageCircleWarning } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from '~/common/components/ui/button';
import type { Language } from '~/common/types/language';
import type { Area} from '~/common/types/area';
import { TgArea } from '~/common/types/area';

export function DefaultErrorBoundary() {
  const error = useRouteError();
  const { t } = useTranslation();
  const { lang, area } = useParams() as { lang: Language; area: Area };
  const currentLang = lang ?? 'de';
  const currentArea = area ?? TgArea;
  const path = `/${currentLang}/${currentArea}`;

  if (!isRouteErrorResponse(error)) {
    Sentry.captureException(error);
  }

  const errorData = isRouteErrorResponse(error) ? extractErrorData(error.data) : undefined;
  const traceId = errorData?.traceId;
  const detailMessage =
    errorData?.message ??
    extractMessageFromError(error) ??
    t('Ein technischer Fehler ist aufgetreten. Bitte laden Sie den Webshop neu!');

  return (
    <div className="flex justify-center items-center h-96 w-full p-8">
      <div className={'p-4'}>
        <MessageCircleWarning size={96} className="text-red-500 mb-4" />
        <div>
          <h1>{t('Ein Fehler ist aufgetreten!')}</h1>
          <p>{detailMessage}</p>
          {traceId && (
            <p className="text-xs text-gray-500 mt-2">
              ID: <code>{traceId}</code>
            </p>
          )}
        </div>
        <div className="flex gap-2 mt-4">
          <Button
            variant={'default'}
            onClick={() => {
              window.location.href = path;
            }}
          >
            {t('Zur Startseite')}
          </Button>
        </div>
      </div>
    </div>
  );
}

function extractErrorData(data: unknown): { message?: string; traceId?: string } | undefined {
  if (typeof data === 'object' && data !== null) {
    const d = data as { message?: unknown; traceId?: unknown };
    return {
      message: typeof d.message === 'string' ? d.message : undefined,
      traceId: typeof d.traceId === 'string' ? d.traceId : undefined
    };
  }
  if (typeof data === 'string' && data.length > 0) {
    return { message: data };
  }
  return undefined;
}

function extractMessageFromError(error: unknown): string | undefined {
  const message = (error as { message?: unknown })?.message;
  return typeof message === 'string' ? message : undefined;
}
